/**
 * useEventTicketTypes Hook
 *
 * Fetches event with full ticket type details for cash ticket issuance.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const EventTicketTypesDocument = `
  query EventTicketTypes($id: ID!) {
    event(id: $id) {
      id
      title
      ticketTypes {
        id
        name
        description
        price
        totalPrice
        currency
        maxQuantity
        soldCount
        available
        isActive
        prices {
          id
          currency
          price
        }
        fees {
          id
          isActive
          overrideAmount
          feeConfiguration {
            id
            type
            calculation
            amount
            currency
            prices {
              currency
              amount
            }
          }
        }
        feesBreakdown {
          name
          type
          amount
        }
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface TicketTypePrice {
  id: string;
  currency: string;
  price: string;
}

export interface FeeConfigPrice {
  currency: string;
  amount: string;
}

export interface FeeConfiguration {
  id: string;
  type: string;
  calculation: string;
  amount: string;
  currency: string;
  prices: FeeConfigPrice[];
}

export interface TicketTypeFee {
  id: string;
  isActive: boolean;
  overrideAmount: string | null;
  feeConfiguration: FeeConfiguration;
}

export interface FeeBreakdown {
  name: string;
  type: string;
  amount: string;
}

export interface EventTicketType {
  id: string;
  name: string;
  description: string | null;
  price: string | null;
  totalPrice: string | null;
  currency: string | null;
  maxQuantity: number | null;
  soldCount: number;
  available: number | null;
  isActive: boolean;
  prices: TicketTypePrice[];
  fees: TicketTypeFee[];
  feesBreakdown: FeeBreakdown[];
}

export interface EventWithTicketTypes {
  id: string;
  title: string;
  ticketTypes: EventTicketType[];
}

interface UseEventTicketTypesOptions {
  eventId: string;
  pause?: boolean;
}

interface UseEventTicketTypesResult {
  event: EventWithTicketTypes | null;
  ticketTypes: EventTicketType[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch event with full ticket type details including prices and fees.
 * Used for cash ticket issuance where admin selects ticket types.
 */
export function useEventTicketTypes(
  options: UseEventTicketTypesOptions
): UseEventTicketTypesResult {
  const { eventId, pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    event: EventWithTicketTypes | null;
  }>({
    query: EventTicketTypesDocument,
    variables: { id: eventId },
    pause: pause || !eventId,
  });

  const event = result.data?.event ?? null;

  return {
    event,
    ticketTypes: event?.ticketTypes ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
